import { Location } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Configuracoes } from "src/app/core/entities/configuracoes";
import { CartService } from "src/app/core/services/cart-service";
import { PedidoService } from "src/app/core/services/pedido-service";
import {
  PRELOADED_PRODUCT_NAV_STATE,
  ProductService,
} from "src/app/core/services/product-service";
import { getOrderItemFromProduct } from "src/app/modules/catalog/components/shared/function";
import { ToastrService } from "ngx-toastr";
import Swal from "sweetalert2";
import { Subject, Subscription, catchError, concatMap, finalize, of, tap } from "rxjs";
import {
  CorteBlocoApi,
  CorteCalculoContexto,
  CorteExcluirEvento,
  CorteItem,
  CorteService,
  ProdutoCorteInfo,
  ResumoCorte,
} from "../../services/corte.service";
import { CorteSyncApiService } from "../../services/corte-sync-api.service";
import {
  CorteModalidadeSelecao,
  TipoServicoCorte,
} from "../../components/corte-modalidade-venda/corte-modalidade-venda.component";
import {
  CorteDevolucaoAcao,
} from "../../components/corte-devolucao-controle/corte-devolucao-controle.component";

@Component({
  selector: "app-page-produto-corte",
  templateUrl: "./page-produto-corte.component.html",
  styleUrls: ["./page-produto-corte.component.css"],
})
export class PageProdutoCorteComponent implements OnInit, OnDestroy {
  product: any = null;
  carregando = true;
  itens: CorteItem[] = [];
  editandoId: string | null = null;
  isSalvando = false;
  persistenciaComErro = false;

  corSelecionada: number = 0;
  tamanhoSelecionado: string = "U";

  /** Estado da modalidade de venda — pronto para integração com API/cálculo de sobra. */
  modalidadeVendaSelecao: CorteModalidadeSelecao | null = null;

  /** Bloco `corte` do último GET produto (barras, totais de sobra, etc.). */
  corteBlocoApi: CorteBlocoApi | null = null;


  private persistir$ = new Subject<void>();
  private subs = new Subscription();

  private navigationPreloadedProduct: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private productService: ProductService,
    private pedidoService: PedidoService,
    private cartService: CartService,
    private corte: CorteService,
    private corteSyncApi: CorteSyncApiService,
    private toast: ToastrService
  ) {
    const nav = this.router.getCurrentNavigation();
    this.navigationPreloadedProduct =
      nav?.extras?.state?.[PRELOADED_PRODUCT_NAV_STATE] ?? null;
  }

  ngOnInit(): void {
    this.subs.add(
      this.persistir$.pipe(concatMap(() => this.persistirAgora())).subscribe()
    );

    this.route.params.subscribe((params) => {
      const id = params["id"];
      if (!id) {
        this.router.navigate(["/notfound"]);
        return;
      }
      this.carregarProduto(id);
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get editando(): CorteItem | null {
    if (!this.editandoId) {
      return null;
    }
    return this.itens.find((i) => i.id === this.editandoId) ?? null;
  }

  get tituloProduto(): string {
    const p = this.product;
    const ref = String(p?.referencia ?? p?.reference ?? p?.ref ?? "").trim();
    const desc = String(p?.descricao ?? p?.nome ?? "").trim();
    if (ref && desc) {
      return `${ref} - ${desc}`;
    }
    return ref || desc || "Produto";
  }

  get resumo() {
    const calc = this.corte.calcularResumo(this.itens, this.produtoResumo);
    return this.corteBlocoApi
      ? this.corte.aplicarResumoCorteApi(calc, this.corteBlocoApi, this.produtoResumo)
      : calc;
  }

  get produtoResumo(): ProdutoCorteInfo | null {
    const p = this.product;
    if (!p) {
      return null;
    }
    const estoques = Array.isArray(p?.estoques) ? p.estoques : [];
    const filialPedidoId = Number(this.pedidoService.pedido?.filial?.id ?? 0) || 0;

    const estoquesFiltrados =
      filialPedidoId > 0
        ? estoques.filter((e: any) => Number(e?.filial ?? 0) === filialPedidoId)
        : estoques;

    const estoqueKg = estoquesFiltrados.reduce((s: number, e: any) => {
      const q = Number(e?.quantidade ?? 0);
      return s + (Number.isFinite(q) ? q : 0);
    }, 0);

    const unidadeConversaoRaw =
      p?.unidade_de_conversao ??
      p?.unidade_conversao ??
      p?.unidadeConversao ??
      p?.fator_conversao ??
      p?.fatorConversao ??
      null;
    const unidadeConversao = Number(unidadeConversaoRaw);
    return {
      fatorConversao: p.fator_conversao ?? p.fatorConversao ?? null,
      estoqueKg: Number.isFinite(estoqueKg) ? estoqueKg : 0,
      unidadeConversao:
        Number.isFinite(unidadeConversao) && unidadeConversao > 0
          ? unidadeConversao
          : null,
      comprimentoProduto:
        this.corte.extrairTamanhoBarra(p, this.corteBlocoApi) ??
        p.comprimento_util ??
        null,
      permiteFracionar:
        p.permite_venda_fracionada ??
        p.permiteVendaFracionada ??
        p.permite_fracionado ??
        p.permite_fracionar ??
        p.permiteFracionar ??
        null,
    };
  }

  /**
   * Preço unitário para o contexto do corte (tabela/cor/estampa/tamanho).
   * O response desse endpoint traz preços em `product.precos[]`.
   */
  get medidasFracionadoCorte(): number[] {
    return this.corte.extrairMedidasFracionado(this.product);
  }

  get medidasIntegralCorte(): number[] {
    const medida = this.corte.extrairTamanhoBarra(this.product, this.corteBlocoApi);
    return medida != null ? [medida] : [];
  }

  get exibirModalidadeVenda(): boolean {
    return !!this.product;
  }

  get utilizacaoEstoqueModalidade(): number {
    const estoqueMt = this.corte.calcularEstoqueMetros(this.produtoResumo);
    const totalApi = Number(this.corteBlocoApi?.total_comprado ?? 0);
    const usado =
      this.resumo.totalSolicitado > 0
        ? this.resumo.totalSolicitado
        : Number.isFinite(totalApi) && totalApi > 0
          ? totalApi
          : this.modalidadeVendaSelecao?.tamanhoMetros ??
            this.medidasFracionadoCorte[0] ??
            this.medidasIntegralCorte[0] ??
            0;
    return this.corte.calcularUtilizacaoEstoquePercentual(estoqueMt, usado);
  }

  get valorServicoCorteExibido(): number {
    return this.corte.extrairValorServicoCorte(this.product, this.corteBlocoApi);
  }

  get precoServicoCorteExibido(): number {
    return this.corte.extrairPrecoServicoCorte(this.product, this.corteBlocoApi);
  }

  /** `tipo_cobranca` do bloco corte → opção exibida em modalidade de venda. */
  get tipoServicoCorteInicial(): TipoServicoCorte {
    return this.corte.mapCobrancaParaTipoServico(this.corteBlocoApi?.tipo_cobranca);
  }

  /** Preço unitário do resumo (`preco_unitario` da API). */
  get precoUnitarioCorte(): number {
    return this.corte.extrairPrecoUnitarioCorte(
      this.product,
      this.corteBlocoApi,
      this.precoTabelaCorte
    );
  }

  get precoTabelaCorte(): number {
    const p = this.product;
    const precos = Array.isArray(p?.precos) ? p.precos : [];
    if (!precos.length) {
      return 0;
    }

    const tabelaId = this.pedidoService.pedido?.tabela?.id ?? null;
    const cor = this.corSelecionada ?? p?.cor ?? p?.cores?.[0]?.id ?? 0;
    const estampa = p?.estampa ?? p?.estampas?.[0]?.id ?? 0;
    const tamanho =
      this.tamanhoSelecionado ??
      p?.tamanho ??
      (p?.tamanhos?.[0]?.tamanho != null ? p.tamanhos[0].tamanho : p?.tamanhos?.[0]) ??
      "U";

    const matchesGrade = (x: any) =>
      Number(x?.cor ?? 0) === Number(cor ?? 0) &&
      Number(x?.estampa ?? 0) === Number(estampa ?? 0) &&
      String(x?.tamanho ?? "U") === String(tamanho ?? "U");

    const candidatos = precos.filter(matchesGrade);
    const pick =
      (tabelaId != null
        ? candidatos.find((x: any) => Number(x?.tabela) === Number(tabelaId))
        : null) ??
      candidatos[0] ??
      (tabelaId != null
        ? precos.find((x: any) => Number(x?.tabela) === Number(tabelaId))
        : null) ??
      precos[0];

    const precoPromocional = Number(pick?.precoPromocional ?? pick?.preco_promocional ?? 0);
    const preco = Number(pick?.preco ?? 0);
    const escolhido =
      Number.isFinite(precoPromocional) && precoPromocional > 0 ? precoPromocional : preco;
    return Number.isFinite(escolhido) ? escolhido : 0;
  }

  /** Lista/catálogo pode indicar corte, mas GET detalhe às vezes não traz tipo_venda/modo_venda. */
  private deveForcarModoCorte(): boolean {
    const q = this.route.snapshot.queryParamMap.get("corte");
    return q === "1" || q === "true";
  }

  private carregarProduto(id: string): void {
    this.carregando = true;
    this.product = null;
    this.itens = [];
    this.corteBlocoApi = null;
    this.editandoId = null;
    const forcarCorte = this.deveForcarModoCorte();

    const pre = this.navigationPreloadedProduct;
    if (pre != null && String(pre.id) === String(id)) {
      this.navigationPreloadedProduct = null;
      this.carregando = false;
      if (!this.corte.deveUsarPaginaCorte(pre, forcarCorte)) {
        this.router.navigate(["/produto", id], {
          state: { [PRELOADED_PRODUCT_NAV_STATE]: pre },
        });
        return;
      }
      this.product =
        forcarCorte && !this.corte.produtoEhModoCorte(pre)
          ? { ...pre, tipo_venda: "corte" }
          : pre;
      this.inicializarVariacoesSelecionadas();
      this.hidratarCortesAposCarregarProduto();
      return;
    }

    this.productService.findById(
      id,
      (p: any) => {
        this.carregando = false;
        if (!this.corte.deveUsarPaginaCorte(p, forcarCorte)) {
          this.router.navigate(["/produto", id]);
          return;
        }
        this.product =
          forcarCorte && !this.corte.produtoEhModoCorte(p)
            ? { ...p, tipo_venda: "corte" }
            : p;
        this.inicializarVariacoesSelecionadas();
        this.hidratarCortesAposCarregarProduto();
      },
      () => {
        this.carregando = false;
        this.router.navigate(["/notfound"]);
      }
    );
  }

  get coresProduto(): { id: number; descricao?: string | null }[] {
    const p = this.product;
    const arr = Array.isArray(p?.cores) ? p.cores : [];
    const mapped = arr
      .map((c: any) => ({
        id: Number(c?.id ?? c?.cor ?? c ?? 0) || 0,
        descricao: c?.descricao ?? c?.nome ?? null,
      }))
      .filter((c: any) => c.id > 0);
    if (mapped.length) {
      return mapped;
    }
    const single = Number(p?.cor ?? 0) || 0;
    return single > 0 ? [{ id: single, descricao: null }] : [{ id: 0, descricao: null }];
  }

  get tamanhosProduto(): string[] {
    const p = this.product;
    const arr = Array.isArray(p?.tamanhos) ? p.tamanhos : [];
    const mapped = arr
      .map((t: any) =>
        String(t?.tamanho != null ? t.tamanho : t != null ? t : "").trim()
      )
      .filter((t: string) => !!t);
    if (mapped.length) {
      return Array.from(new Set(mapped));
    }
    const single = String(p?.tamanho ?? "U").trim();
    return [single || "U"];
  }

  private inicializarVariacoesSelecionadas(): void {
    const p = this.product;
    if (!p) {
      this.corSelecionada = 0;
      this.tamanhoSelecionado = "U";
      return;
    }

    const pedido = this.pedidoService.pedido;
    const itemPedido =
      pedido?.itens?.find?.((i: any) => Number(i?.produto) === Number(p?.id)) ?? null;
    const corPedido = Number(itemPedido?.cor ?? 0) || 0;
    const tamanhoPedido = String(itemPedido?.tamanho ?? "").trim();

    const corProduto = Number(p?.cor ?? 0) || 0;
    this.corSelecionada =
      corPedido || corProduto || (this.coresProduto[0]?.id ?? 0) || 0;

    const tamanhoProduto = String(p?.tamanho ?? "").trim();
    this.tamanhoSelecionado =
      tamanhoPedido || tamanhoProduto || (this.tamanhosProduto[0] ?? "U") || "U";
  }

  voltar(): void {
    if (this.isSalvando) {
      this.toast.warning(
        "Aguarde o salvamento dos cortes no servidor antes de sair.",
        "Corte",
        { timeOut: 3500, positionClass: "toast-bottom-right" }
      );
      return;
    }
    // Não usar history.back(): pode voltar para telas que fazem refresh (ex.: pedido/listaPedido).
    // O catálogo restaura grade + scroll via ProductService.consumeStashedCatalogUi().
    const returnUrl = this.productService.peekStashedCatalogReturnUrl();
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl, { replaceUrl: true });
    } else {
      this.router.navigate(["/catalogo"], { replaceUrl: true });
    }
  }

  onCancelarEdicaoForm(): void {
    if (this.isSalvando) {
      return;
    }
    this.editandoId = null;
  }

  onModalidadeVendaChange(selecao: CorteModalidadeSelecao): void {
    const tipoAnterior = this.modalidadeVendaSelecao?.tipoServicoCorte;
    this.modalidadeVendaSelecao = selecao;

    const usuarioAlterouServico =
      selecao.origem === "usuario" &&
      tipoAnterior != null &&
      tipoAnterior !== selecao.tipoServicoCorte;

    if (usuarioAlterouServico && this.itens.length > 0 && !this.isSalvando) {
      this.solicitarPersistencia([...this.itens]);
    }
  }

  /** Devolução de sobra elegível (modal + POST v2/pedido/itens com `devolvido`). */
  onDevolucaoAcao(ev: CorteDevolucaoAcao): void {
    if (this.isSalvando) {
      return;
    }
    if (ev.tipo === "devolver") {
      this.confirmarDevolucaoElegivel(ev);
      return;
    }
    if (ev.tipo === "solicitar_autorizacao") {
      this.confirmarDevolucaoNaoElegivel(ev);
      return;
    }
    if (ev.tipo === "estornar") {
      this.confirmarEstorno(ev);
      return;
    }
  }

  private confirmarEstorno(ev: CorteDevolucaoAcao): void {
    if (!ev.alvoEstorno || ev.metros <= 0) {
      this.toast.warning(
        "Selecione o tipo e a quantidade para estornar.",
        "Estorno",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }
    const tipoLabel = ev.alvoEstorno === "barra" ? "barra (elegível)" : "resíduo";
    const metrosFmt = this.corte.formatarMetros(ev.metros);
    Swal.fire({
      title: "Confirmar estorno",
      html: `Deseja estornar <strong>${metrosFmt}&nbsp;m</strong> de devolução de <strong>${tipoLabel}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, estornar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#6c757d",
      cancelButtonColor: "#adb5bd",
    }).then((result) => {
      if (result.isConfirmed) {
        this.enviarEstorno(ev);
      }
    });
  }

  private enviarEstorno(ev: CorteDevolucaoAcao): void {
    if (!ev.alvoEstorno || ev.metros <= 0) {
      return;
    }

    const opcoes: {
      devolvido?: number;
      devolvido_nao_elegivel?: number;
    } = {};

    if (ev.alvoEstorno === "barra") {
      const atual = Number(this.corteBlocoApi?.devolvido ?? 0);
      opcoes.devolvido = this.corte.arredondar2(
        Math.max(0, (Number.isFinite(atual) && atual >= 0 ? atual : 0) - ev.metros)
      );
    } else {
      const atual = Number(this.corteBlocoApi?.devolvido_nao_elegivel ?? 0);
      opcoes.devolvido_nao_elegivel = this.corte.arredondar2(
        Math.max(
          0,
          (Number.isFinite(atual) && atual >= 0 ? atual : 0) - ev.metros
        )
      );
    }

    const payload = this.gerarPayloadSyncCortes(opcoes);
    if (!payload) {
      this.toast.warning(
        "Não foi possível montar o pedido para estorno.",
        "Estorno",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }

    const tipoLabel = ev.alvoEstorno === "barra" ? "barra" : "resíduo";
    this.isSalvando = true;
    this.corteSyncApi
      .salvarItens(payload)
      .pipe(
        tap((resp) => {
          this.aplicarCorteDaRespostaSync(resp);
          this.atualizarCarrinhoComTotalMetros();
          this.toast.success(
            `Estorno de ${this.corte.formatarMetros(ev.metros)} m (${tipoLabel}) registrado.`,
            "Estorno",
            { timeOut: 4000, positionClass: "toast-bottom-right" }
          );
        }),
        catchError((err) => {
          const msg =
            err?.error?.detail ||
            err?.error?.message ||
            "Não foi possível registrar o estorno no servidor.";
          this.toast.error(String(msg), "Estorno", {
            positionClass: "toast-bottom-left",
          });
          return of(null);
        }),
        finalize(() => {
          this.isSalvando = false;
        })
      )
      .subscribe();
  }

  private confirmarDevolucaoElegivel(ev: CorteDevolucaoAcao): void {
    if (ev.metros <= 0) {
      this.toast.warning(
        "Selecione uma quantidade maior que zero para devolver.",
        "Devolução",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }
    const metrosFmt = this.corte.formatarMetros(ev.metros);
    Swal.fire({
      title: "Confirmar devolução",
      html: `Deseja devolver <strong>${metrosFmt}&nbsp;m</strong> de sobra elegível?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, devolver",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        this.enviarDevolucaoElegivel(ev.metros);
      }
    });
  }

  private enviarDevolucaoElegivel(metrosDevolver: number): void {
    const atual = Number(this.corteBlocoApi?.devolvido ?? 0);
    const novoDevolvido =
      (Number.isFinite(atual) && atual >= 0 ? atual : 0) + metrosDevolver;

    const payload = this.gerarPayloadSyncCortes({ devolvido: novoDevolvido });
    if (!payload) {
      this.toast.warning(
        "Não foi possível montar o pedido para devolução.",
        "Devolução",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }

    this.isSalvando = true;
    this.corteSyncApi
      .salvarItens(payload)
      .pipe(
        tap((resp) => {
          this.aplicarCorteDaRespostaSync(resp);
          this.atualizarCarrinhoComTotalMetros();
          this.toast.success(
            `Devolução de ${this.corte.formatarMetros(metrosDevolver)} m registrada.`,
            "Devolução",
            { timeOut: 4000, positionClass: "toast-bottom-right" }
          );
        }),
        catchError((err) => {
          const msg =
            err?.error?.detail ||
            err?.error?.message ||
            "Não foi possível registrar a devolução no servidor.";
          this.toast.error(String(msg), "Devolução", {
            positionClass: "toast-bottom-left",
          });
          return of(null);
        }),
        finalize(() => {
          this.isSalvando = false;
        })
      )
      .subscribe();
  }

  private confirmarDevolucaoNaoElegivel(ev: CorteDevolucaoAcao): void {
    if (ev.metros <= 0) {
      this.toast.warning(
        "Selecione uma quantidade maior que zero para devolver.",
        "Devolução",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }
    const metrosFmt = this.corte.formatarMetros(ev.metros);
    Swal.fire({
      title: "Confirmar devolução não elegível",
      html: `Deseja registrar <strong>${metrosFmt}&nbsp;m</strong> de sobra não elegível?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, confirmar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        this.enviarDevolucaoNaoElegivel(ev.metros);
      }
    });
  }

  private enviarDevolucaoNaoElegivel(metrosDevolver: number): void {
    const atual = Number(this.corteBlocoApi?.devolvido_nao_elegivel ?? 0);
    const novoDevolvidoNaoElegivel =
      (Number.isFinite(atual) && atual >= 0 ? atual : 0) + metrosDevolver;

    const payload = this.gerarPayloadSyncCortes({
      devolvido_nao_elegivel: novoDevolvidoNaoElegivel,
    });
    if (!payload) {
      this.toast.warning(
        "Não foi possível montar o pedido para devolução.",
        "Devolução",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }

    this.isSalvando = true;
    this.corteSyncApi
      .salvarItens(payload)
      .pipe(
        tap((resp) => {
          this.aplicarCorteDaRespostaSync(resp);
          this.atualizarCarrinhoComTotalMetros();
          this.toast.success(
            `Devolução não elegível de ${this.corte.formatarMetros(metrosDevolver)} m registrada.`,
            "Devolução",
            { timeOut: 4000, positionClass: "toast-bottom-right" }
          );
        }),
        catchError((err) => {
          const msg =
            err?.error?.detail ||
            err?.error?.message ||
            "Não foi possível registrar a devolução no servidor.";
          this.toast.error(String(msg), "Devolução", {
            positionClass: "toast-bottom-left",
          });
          return of(null);
        }),
        finalize(() => {
          this.isSalvando = false;
        })
      )
      .subscribe();
  }

  /**
   * Atualiza sobras/devoluções com `total_sobra` retornado pelo POST v2/pedido/itens.
   */
  private contextoCalculoCorteSync(): Omit<CorteCalculoContexto, "produtoId"> {
    const p = this.product;
    const pc = this.pedidoService.pedido?.clientes?.[0];
    return {
      cor: Number(this.corSelecionada ?? p?.cor ?? 0) || 0,
      estampa: Number(p?.estampa ?? p?.estampas?.[0]?.id ?? 0) || 0,
      tamanho: String(this.tamanhoSelecionado ?? p?.tamanho ?? "U").trim() || "U",
      pedidoClienteId: Number(pc?.id ?? 0) || undefined,
      clienteId: Number(pc?.cliente?.id ?? pc?.cliente ?? 0) || undefined,
    };
  }

  private aplicarCorteDaRespostaSync(resp: any): void {
    const pid = Number(this.product?.id ?? 0);
    if (!pid) {
      return;
    }
    const ctx = this.contextoCalculoCorteSync();
    const bloco = this.corte.extrairBlocoCorteDeRespostaSync(resp, pid, ctx);

    if (bloco) {
      this.aplicarBlocoCorteNaTela(bloco);
      return;
    }

    // Não reidratar de observacaoCorte local — fica obsoleto após sync/exclusão.
    if (
      this.itens.length === 0 ||
      this.corte.respostaSyncIndicaCorteRemovido(resp, ctx)
    ) {
      this.limparEstadoCorteLocal();
      this.removerLinhaCorteDoPedidoEmMemoria(pid);
    }
  }

  private aplicarBlocoCorteNaTela(bloco: CorteBlocoApi): void {
    const blocoNovo =
      this.corte.mesclarBlocoCorteApi(this.corteBlocoApi, bloco) ?? bloco;
    this.corteBlocoApi = blocoNovo;
    this.product = {
      ...this.product,
      corte: blocoNovo,
      peso_total_liquido: blocoNovo.peso_total_liquido,
      preco_unitario: blocoNovo.preco_unitario,
      valor_total: blocoNovo.valor_total,
    };

    this.sincronizarItensComBloco(blocoNovo);
  }

  /** Mantém `itens` alinhados a `corte.barras` sem perder ids estáveis. */
  private sincronizarItensComBloco(bloco: CorteBlocoApi | null): void {
    if (!bloco) {
      return;
    }
    const itensApi = this.corte.itensDeBlocoCorte(bloco, this.itens);
    if (itensApi.length > 0) {
      this.itens = itensApi;
    }
  }

  /** Zera lista, bloco API e `product.corte` (GET com `corte: null` ou sync sem cálculo). */
  private limparEstadoCorteLocal(): void {
    this.itens = [];
    this.corteBlocoApi = null;
    this.editandoId = null;
    if (this.product) {
      this.product = { ...this.product, corte: null };
    }
  }

  private removerLinhaCorteDoPedidoEmMemoria(produtoId: number): void {
    const pedido = this.pedidoService.pedido;
    if (!pedido?.itens?.length || !produtoId) {
      return;
    }
    const p = this.product;
    const cor = Number(this.corSelecionada ?? p?.cor ?? 0) || 0;
    const estampa = Number(p?.estampa ?? p?.estampas?.[0]?.id ?? 0) || 0;
    const tamanho =
      String(this.tamanhoSelecionado ?? p?.tamanho ?? "U").trim() || "U";
    const clienteId = Number(pedido.clientes?.[0]?.cliente?.id ?? 0) || 0;

    const idx = pedido.itens.findIndex(
      (i: any) =>
        Number(i?.produto) === Number(produtoId) &&
        Number(i?.cor ?? 0) === cor &&
        Number(i?.estampa ?? 0) === estampa &&
        String(i?.tamanho ?? "") === tamanho &&
        (!clienteId || Number(i?.cliente) === clienteId)
    );
    if (idx < 0) {
      return;
    }
    pedido.itens.splice(idx, 1);
    this.pedidoService.recalculaPedido();
    this.pedidoService.refresh();
  }

  onAdicionar(payload: {
    qtd: number;
    comprimento_util: number;
    cor?: number | null;
    tamanho?: string | null;
  }): void {
    if (this.isSalvando) {
      return;
    }
    if (payload?.cor != null) {
      const c = Number(payload.cor);
      if (Number.isFinite(c)) {
        this.corSelecionada = c;
      }
    }
    if (payload?.tamanho != null) {
      const t = String(payload.tamanho).trim();
      if (t) {
        this.tamanhoSelecionado = t;
      }
    }
    const { qtd, comprimento_util } = payload;
    if (this.editandoId) {
      const before = this.itens;
      const filtrados = this.itens.filter((i) => i.id !== this.editandoId);
      const atualizado: CorteItem = {
        id: this.editandoId,
        qtd,
        comprimento_util,
      };
      this.itens = this.corte.agrupar([...filtrados, atualizado]);
      this.flashLinha(this.editandoId);
      this.editandoId = null;
      this.solicitarPersistencia(before);
      return;
    }
    const before = this.itens;
    const novo: CorteItem = {
      id: this.corte.novoId(),
      qtd,
      comprimento_util,
    };
    this.itens = this.corte.agrupar([...this.itens, novo]);
    const alvo = this.itens.find(
      (m) => Math.abs(m.comprimento_util - comprimento_util) < 1e-9
    );
    if (alvo) {
      setTimeout(() => this.flashLinha(alvo.id), 0);
    }
    this.solicitarPersistencia(before);
  }

  onEditar(id: string): void {
    if (this.isSalvando) {
      return;
    }
    this.editandoId = id;
  }

  onItensDerivados(itens: CorteItem[]): void {
    if (!itens?.length || this.itens.length > 0) {
      return;
    }
    this.itens = itens;
  }

  onExcluir(ev: CorteExcluirEvento): void {
    if (this.isSalvando) {
      return;
    }
    const item = this.itens.find((i) => i.id === ev.id);
    if (!item) {
      return;
    }
    const before = [...this.itens];
    const qtdLinha = Math.max(0, Math.floor(Number(ev.qtd) || 0));
    if (qtdLinha > 0 && qtdLinha < item.qtd) {
      this.itens = this.itens.map((it) =>
        it.id === ev.id ? { ...it, qtd: it.qtd - qtdLinha } : it
      );
    } else {
      this.itens = this.itens.filter((i) => i.id !== ev.id);
    }
    if (this.editandoId === ev.id && !this.itens.some((i) => i.id === ev.id)) {
      this.editandoId = null;
    }
    this.solicitarPersistencia(before);
  }

  /**
   * Ao adicionar/editar/excluir cortes, atualiza o carrinho localmente para refletir
   * a quantidade total em metros (soma dos cortes, com ajuste de sobra quando aplicável).
   *
   * Observação: isso atualiza o pedido em memória para feedback imediato no carrinho
   * (sem precisar apertar "Confirmar").
   */
  private atualizarCarrinhoComTotalMetros(): void {
    const pedido = this.pedidoService.pedido;
    if (!pedido?.clientes?.length || !this.product?.id) {
      return;
    }

    if (this.itens.length === 0) {
      this.removerLinhaCorteDoPedidoEmMemoria(Number(this.product.id));
      return;
    }

    const clienteId = pedido.clientes[0].cliente.id;
    const montado = this.montarItemCarrinhoCorte(clienteId);
    if (!montado) {
      return;
    }
    const { item } = montado;

    // Upsert no pedido atual.
    const idx = pedido.itens.findIndex(
      (i: any) =>
        Number(i?.produto) === Number(item?.produto) &&
        Number(i?.cor ?? 0) === Number(item?.cor ?? 0) &&
        Number(i?.estampa ?? 0) === Number(item?.estampa ?? 0) &&
        String(i?.tamanho ?? "") === String(item?.tamanho ?? "") &&
        Number(i?.cliente) === Number(item?.cliente)
    );
    if (idx >= 0) {
      pedido.itens[idx] = { ...pedido.itens[idx], ...item };
    } else {
      pedido.itens.push(item);
    }

    this.pedidoService.recalculaPedido();
    this.pedidoService.refresh();
  }

  private solicitarPersistencia(before: CorteItem[]): void {
    // Otimista: reflete na tela imediatamente, mas com rollback se falhar.
    // Guardamos snapshot para reverter se necessário.
    (this as any)._itensBeforePersist = before;
    this.persistir$.next();
  }

  private persistirAgora() {
    this.isSalvando = true;
    const payload = this.gerarPayloadSyncCortes();
    if (!payload) {
      this.isSalvando = false;
      return of(null);
    }
    this.persistenciaComErro = false;
    return this.corteSyncApi.salvarItens(payload).pipe(
      tap((resp) => {
        this.aplicarCorteDaRespostaSync(resp);
        this.atualizarCarrinhoComTotalMetros();
      }),
      catchError((err) => {
        this.persistenciaComErro = true;
        const before: CorteItem[] | undefined = (this as any)._itensBeforePersist;
        if (before) {
          this.itens = before;
        }
        const msg =
          err?.error?.detail ||
          err?.error?.message ||
          "Não foi possível salvar os cortes no servidor.";
        this.toast.error(String(msg), "Corte", { positionClass: "toast-bottom-left" });
        return of(null);
      }),
      finalize(() => {
        this.isSalvando = false;
      })
    );
  }

  private gerarPayloadSyncCortes(opcoes?: {
    devolvido?: number;
    devolvido_nao_elegivel?: number;
  }): any | null {
    const pedido = this.pedidoService.pedido;
    if (!pedido?.clientes?.length) {
      return null;
    }
    if (!this.product?.id) {
      return null;
    }

    const pc = pedido.clientes[0];
    const cliente_id = pc?.cliente?.id ?? pc?.cliente ?? null;
    const pedido_id = pc?.id ?? pedido?.id ?? null;
    if (!cliente_id || !pedido_id) {
      return null;
    }

    const quantidadeTotal = this.itens.reduce(
      (s, it) => s + (Number(it.qtd) || 0),
      0
    );

    const tamanhoLinha: Record<string, unknown> = {
      tamanho: String(this.tamanhoSelecionado ?? "U") || "U",
      quantidade: quantidadeTotal,
    };

    // Exclusão (cliente desistiu): quantidade 0 na grade, sem detalhe.
    // cortes: [] ou eco do GET com barras/totais é rejeitado pela API.
    if (quantidadeTotal > 0) {
      const detalhe = this.corte.montarDetalheCorteSync(
        this.itens,
        this.corteBlocoApi,
        this.corte.extrairTamanhoBarra(this.product, this.corteBlocoApi),
        {
          devolvido: opcoes?.devolvido,
          devolvido_nao_elegivel: opcoes?.devolvido_nao_elegivel,
          tipo_cobranca: this.corte.mapTipoServicoParaCobranca(
            this.modalidadeVendaSelecao?.tipoServicoCorte
          ),
        }
      );
      if (detalhe) {
        tamanhoLinha.detalhe = detalhe;
      }
    }

    return {
      clientes: [
        {
          cliente_id: Number(cliente_id),
          pedido_id: Number(pedido_id),
          modo: "sync",
          grade: [
            {
              produto: Number(this.product.id),
              variacoes: [
                {
                  cor: Number(this.corSelecionada ?? 0) || 0,
                  tamanhos: [tamanhoLinha],
                },
              ],
            },
          ],
        },
      ],
    };
  }

  /**
   * Ordem: 1) cortes já salvos no item do pedido (observação); 2) objeto `corte` do GET produto/:id.
   */
  private hidratarCortesAposCarregarProduto(): void {
    // Fonte da verdade ao entrar na tela: sempre preferir o response do GET produto/:id?pedido_id=...
    // Isso garante que a UI reflita o que o servidor entende como corte atual.
    this.hidratarCortesDoResponseProduto();
    if (this.itens.length === 0) {
      this.hidratarCortesPersistidos();
    }
  }

  /** Preenche a lista a partir de `product.corte` (barras ou formato legado). */
  private hidratarCortesDoResponseProduto(): void {
    const p = this.product;
    if (!p?.id) {
      return;
    }
    this.corteBlocoApi = this.corte.extrairBlocoCorte(p);
    this.sincronizarItensComBloco(this.corteBlocoApi);
  }

  private hidratarCortesPersistidos(): void {
    const p = this.product;
    // GET com pedido_id e `corte: null` é fonte da verdade — não usar observacao local.
    if (p && "corte" in p && p.corte === null) {
      return;
    }
    const pedido = this.pedidoService.pedido;
    if (!pedido?.itens?.length || !this.product?.id) {
      return;
    }
    const line = pedido.itens.find((i: any) => Number(i?.produto) === Number(this.product.id));
    const raw =
      (line as any)?.observacaoCorte ??
      (line as any)?.observacao_corte ??
      (line as any)?.observacao ??
      null;
    if (!raw) {
      return;
    }
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const blocoPersistido: CorteBlocoApi = {
        barras: parsed?.barras,
        cortes: parsed?.cortes,
        total_sobra: parsed?.total_sobra ?? parsed?.sobraTotal,
        total_comprado: parsed?.total_comprado ?? parsed?.totalSolicitado,
        liquido_elegivel:
          parsed?.liquido_elegivel ?? parsed?.total_elegivel ?? parsed?.totalElegivel,
        liquido_nao_elegivel:
          parsed?.liquido_nao_elegivel ??
          parsed?.total_nao_elegivel ??
          parsed?.totalNaoElegivel,
        sobra_liquida: parsed?.sobra_liquida ?? parsed?.sobraLiquida,
        valor_servico_corte:
          parsed?.valor_servico_corte ?? parsed?.valorServicoCorte,
        preco_servico_corte:
          parsed?.preco_servico_corte ?? parsed?.precoServicoCorte,
        preco_unitario: parsed?.preco_unitario ?? parsed?.precoUnitario,
        peso_total_liquido:
          parsed?.peso_total_liquido ?? parsed?.pesoTotalLiquido,
        valor_total: parsed?.valor_total ?? parsed?.valorTotal,
        devolvido: parsed?.devolvido,
        devolvido_nao_elegivel: parsed?.devolvido_nao_elegivel,
        tipo_cobranca: parsed?.tipo_cobranca,
      };
      const itens = this.corte.itensDeBlocoCorte(blocoPersistido, this.itens);
      if (itens.length > 0) {
        this.itens = itens;
        if (!this.corteBlocoApi) {
          this.corteBlocoApi = blocoPersistido;
        }
      } else if (!this.corteBlocoApi) {
        this.itens = [];
      }
    } catch {
      // Se vier inválido, não quebra a tela.
    }
  }

  private flashLinha(id: string): void {
    this.itens = this.itens.map((i) =>
      i.id === id ? { ...i, flash: true } : i
    );
    setTimeout(() => {
      this.itens = this.itens.map((i) =>
        i.id === id ? { ...i, flash: false } : i
      );
    }, 1300);
  }

  confirmar(): void {
    if (this.isSalvando) {
      return;
    }
    if (this.itens.length === 0) {
      this.toast.warning(
        "Adicione ao menos um corte antes de finalizar.",
        "Corte",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }
    const pedido = this.pedidoService.pedido;
    if (!pedido?.clientes?.length) {
      this.toast.warning(
        "Selecione um pedido com cliente para adicionar ao carrinho.",
        "Corte",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }

    const clienteId = pedido.clientes[0].cliente.id;
    const montado = this.montarItemCarrinhoCorte(clienteId);
    if (!montado) {
      this.toast.warning(
        "Não foi possível calcular o preço do corte para o carrinho.",
        "Corte",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
      return;
    }
    const { item } = montado;

    this.pedidoService.setCartItemsSku(
      [item],
      (resp) => {
        if (!Configuracoes.mostraCarrinho) {
          this.toast.success("Item adicionado ao pedido.", "Corte", {
            positionClass: "toast-bottom-right",
          });
        }
        this.cartService.addItems([item]);
        this.pedidoService.select(resp);
        this.location.back();
      },
      (err) => {
        if (err?.status === 400 && err?.error?.detail) {
          this.toast.error(err.error.detail, "Produto", {
            positionClass: "toast-bottom-left",
          });
        } else {
          this.toast.error("Não foi possível adicionar ao pedido.", "Corte");
        }
      }
    );
  }

  /**
   * Preço e total do carrinho — mesma regra do resumo (`preco_unitario` / `valor_total` da API).
   * O GET produto traz `precos[]`, não `preco` na raiz (evita NaN).
   */
  private resolverPrecosCarrinhoCorte(): {
    snap: any;
    price: number;
    qty: number;
    totalLinha: number;
    pesoLiquido: number;
    res: ResumoCorte;
  } | null {
    if (!this.product?.id) {
      return null;
    }
    const res = this.resumo;
    const snap = this.buildCartProductSnapshot();
    const price = Number(snap.preco) || 0;
    const qty = Number(res.totalFinal) || 0;

    let totalLinha = Number(res.valorTotal);
    if (!Number.isFinite(totalLinha) || totalLinha < 0) {
      totalLinha = NaN;
    }
    if (!Number.isFinite(totalLinha) || totalLinha <= 0) {
      totalLinha = this.corte.calcularValorTotalCorte(
        this.product,
        this.corteBlocoApi,
        price,
        res.pesoTotalLiquido ?? res.pesoTotal ?? 0
      );
    }
    if ((!Number.isFinite(totalLinha) || totalLinha <= 0) && price > 0) {
      const peso = Number(res.pesoTotalLiquido ?? res.pesoTotal ?? 0);
      totalLinha =
        peso > 0
          ? this.corte.arredondar2(price * peso)
          : qty > 0
            ? this.corte.arredondar2(qty * price)
            : 0;
    }
    if (!Number.isFinite(totalLinha)) {
      totalLinha = 0;
    }

    const pesoLiquido = this.corte.extrairPesoTotalLiquido(
      this.product,
      this.corteBlocoApi,
      res.pesoTotalLiquido ?? res.pesoTotal ?? 0
    );

    return { snap, price, qty, totalLinha, pesoLiquido, res };
  }

  private montarItemCarrinhoCorte(
    clienteId: number
  ): { item: any } | null {
    const valores = this.resolverPrecosCarrinhoCorte();
    if (!valores) {
      return null;
    }
    const { snap, price, qty, totalLinha, res, pesoLiquido } = valores;
    const tax = snap.imposto;
    let taxCalc: any = {};
    let taxValue = 0;
    if (tax && Number.isFinite(price) && price > 0) {
      taxCalc = this.productService.calculaImposto(
        price,
        tax.ipi,
        tax.alicms,
        tax.substicms,
        tax.alicmsorigem,
        tax.fcpSt
      );
      taxValue =
        this.productService.calculaValorImposto(
          price,
          tax.ipi,
          tax.alicms,
          tax.substicms,
          tax.alicmsorigem,
          tax.fcpSt
        ) ?? 0;
    }

    const item = getOrderItemFromProduct(
      snap,
      taxCalc,
      tax,
      taxValue,
      clienteId,
      qty,
      totalLinha
    ) as any;
    item.tipo_venda = "corte";
    if (pesoLiquido > 0) {
      item.peso_total_liquido = pesoLiquido;
      item.quantidade_convertida = pesoLiquido;
    }
    item.observacaoCorte = JSON.stringify(
      this.corte.observacaoCortePayload(
        this.itens,
        res,
        this.corte.extrairTamanhoBarra(this.product, this.corteBlocoApi)
      )
    );
    return { item };
  }

  /** Objeto compatível com getOrderItemFromProduct a partir do detalhe carregado. */
  private buildCartProductSnapshot(): any {
    const p = this.product;
    const cor = this.corSelecionada ?? p.cor ?? p.cores?.[0]?.id ?? 0;
    const estampa = p.estampa ?? p.estampas?.[0]?.id ?? 0;
    const tamanho =
      this.tamanhoSelecionado ??
      p.tamanho ??
      (p.tamanhos?.[0]?.tamanho != null
        ? p.tamanhos[0].tamanho
        : p.tamanhos?.[0]) ??
      "";
    const precoTabela = this.precoTabelaCorte;
    const precoUnitario = this.corte.extrairPrecoUnitarioCorte(
      p,
      this.corteBlocoApi,
      precoTabela
    );
    const preco =
      Number.isFinite(precoUnitario) && precoUnitario > 0
        ? precoUnitario
        : precoTabela;
    const res = this.resumo;
    const pesoLiquido = this.corte.extrairPesoTotalLiquido(
      p,
      this.corteBlocoApi,
      res.pesoTotalLiquido ?? res.pesoTotal ?? 0
    );
    const metros = Number(res.totalFinal) || 0;
    const img0 = Array.isArray(p?.imagens) ? p.imagens[0] : null;
    const imagemNome = img0?.nome ?? p?.imagem ?? p?.nome ?? null;

    return {
      ...p,
      cor,
      estampa,
      tamanho,
      preco,
      precopromocional: 0,
      referencia: p.referencia ?? p.reference ?? p.ref,
      imagem: imagemNome,
      nome: imagemNome,
      peso_total_liquido: pesoLiquido > 0 ? pesoLiquido : undefined,
      quantidade_digitada: metros > 0 ? metros : undefined,
      quantidade_convertida: pesoLiquido > 0 ? pesoLiquido : undefined,
      unidade_digitada: p.unidade_venda ?? p.unidadeVenda,
      unidade_convertida: p.unidade_estoque ?? p.unidadeEstoque,
    };
  }
}
